import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useWatch, useController } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { vehicleSchema, type VehicleFormData } from '@/validators/vehicle'
import { useVehicle, useCreateVehicle, useUpdateVehicle } from '@/hooks/useVehicles'
import { useDrivers } from '@/hooks/useDrivers'
import { FormSection } from '@/components/form/form-section'
import { PlateInput } from '@/components/form/plate-input'
import { DatePicker } from '@/components/form/date-picker'
import { ComboboxSelect } from '@/components/form/combobox-select'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { cn } from '@/lib/utils'

const VEHICLE_TYPES = [
  { value: 'FIORINO', label: 'Fiorino' },
  { value: 'VAN', label: 'Van' },
  { value: 'VUC', label: 'VUC' },
  { value: 'TOCO', label: 'Toco' },
  { value: 'TRUCK', label: 'Truck' },
  { value: 'BITRUCK', label: 'Bitruck' },
  { value: 'CARRETA', label: 'Carreta' },
  { value: 'RODOTREM', label: 'Rodotrem' },
]

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

export function VehicleFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const { data: vehicle, isLoading: loadingVehicle } = useVehicle(id ?? '')
  const createMutation = useCreateVehicle()
  const updateMutation = useUpdateVehicle(id ?? '')
  const mutation = isEdit ? updateMutation : createMutation

  const { data: driversData } = useDrivers({ active: true, perPage: 200 })
  const driverOptions = (driversData?.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }))

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<VehicleFormData>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      plate: '',
      renavam: '',
      brand: '',
      model: '',
      year: '' as unknown as number,
      vehicleType: 'TOCO',
      ownership: 'COMPANY',
      ownerId: '',
      capacityKg: '' as unknown as number,
      bodyLength: '' as unknown as number,
      bodyWidth: '' as unknown as number,
      bodyHeight: '' as unknown as number,
      rntrc: '',
      crlvNumber: '',
      crlvExpiry: '',
      insuranceCompany: '',
      insurancePolicy: '',
      insuranceExpiry: '',
    },
  })

  const ownership = useWatch({ control, name: 'ownership' })

  const {
    field: ownerIdField,
    fieldState: { error: ownerIdError },
  } = useController({ control, name: 'ownerId' })

  useEffect(() => {
    if (vehicle) {
      reset({
        plate: vehicle.plate,
        renavam: vehicle.renavam ?? '',
        brand: vehicle.brand ?? '',
        model: vehicle.model ?? '',
        year: (vehicle.year ?? '') as unknown as number,
        vehicleType: vehicle.vehicleType,
        ownership: vehicle.ownership,
        ownerId: vehicle.ownerId ?? '',
        capacityKg: (vehicle.capacityKg ? parseFloat(vehicle.capacityKg) : '') as unknown as number,
        bodyLength: (vehicle.bodyLength ? parseFloat(vehicle.bodyLength) : '') as unknown as number,
        bodyWidth: (vehicle.bodyWidth ? parseFloat(vehicle.bodyWidth) : '') as unknown as number,
        bodyHeight: (vehicle.bodyHeight ? parseFloat(vehicle.bodyHeight) : '') as unknown as number,
        rntrc: vehicle.rntrc ?? '',
        crlvNumber: vehicle.crlvNumber ?? '',
        crlvExpiry: vehicle.crlvExpiry ? vehicle.crlvExpiry.slice(0, 10) : '',
        insuranceCompany: vehicle.insuranceCompany ?? '',
        insurancePolicy: vehicle.insurancePolicy ?? '',
        insuranceExpiry: vehicle.insuranceExpiry ? vehicle.insuranceExpiry.slice(0, 10) : '',
      })
    }
  }, [vehicle, reset])

  async function onSubmit(data: VehicleFormData) {
    try {
      await mutation.mutateAsync(data)
      toast.success(isEdit ? 'Veículo atualizado!' : 'Veículo cadastrado!')
      navigate('/vehicles')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      toast.error(msg ?? 'Erro ao salvar veículo')
    }
  }

  if (isEdit && loadingVehicle) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/vehicles')}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? 'Editar veículo' : 'Novo veículo'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? vehicle?.plate : 'Preencha os dados do veículo'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Identificação */}
        <FormSection title="Identificação">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <PlateInput control={control} name="plate" />
            <div className="space-y-1.5">
              <Label htmlFor="renavam">RENAVAM</Label>
              <Input id="renavam" {...register('renavam')} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="brand">Marca</Label>
              <Input id="brand" {...register('brand')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model">Modelo</Label>
              <Input id="model" {...register('model')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="year">Ano</Label>
              <Input
                id="year"
                type="number"
                min={1900}
                max={2099}
                placeholder="2020"
                {...register('year')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vehicleType">Tipo *</Label>
              <select
                id="vehicleType"
                {...register('vehicleType')}
                aria-invalid={!!errors.vehicleType}
                className={cn(selectClass, errors.vehicleType && 'border-destructive')}
              >
                {VEHICLE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {errors.vehicleType && (
                <p className="text-xs text-destructive">{errors.vehicleType.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rntrc">RNTRC / ANTT</Label>
              <Input id="rntrc" {...register('rntrc')} />
            </div>
          </div>
        </FormSection>

        {/* Proprietário */}
        <FormSection title="Proprietário">
          <div className="space-y-1.5">
            <Label htmlFor="ownership">Propriedade *</Label>
            <select
              id="ownership"
              {...register('ownership')}
              aria-invalid={!!errors.ownership}
              className={cn(selectClass, errors.ownership && 'border-destructive')}
            >
              <option value="COMPANY">Empresa</option>
              <option value="DRIVER">Motorista</option>
            </select>
          </div>

          {ownership === 'DRIVER' && (
            <div className="space-y-1.5">
              <Label>Motorista proprietário *</Label>
              <ComboboxSelect
                options={driverOptions}
                value={(ownerIdField.value as string) ?? ''}
                onChange={ownerIdField.onChange}
                placeholder="Selecione o motorista..."
                searchPlaceholder="Buscar motorista..."
                aria-invalid={!!ownerIdError}
              />
              {ownerIdError && (
                <p className="text-xs text-destructive">{ownerIdError.message}</p>
              )}
            </div>
          )}
        </FormSection>

        {/* Capacidade */}
        <FormSection title="Capacidade">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="capacityKg">Peso máx. (kg)</Label>
              <Input
                id="capacityKg"
                type="number"
                step="0.01"
                min="0"
                {...register('capacityKg')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bodyLength">Compr. (m)</Label>
              <Input
                id="bodyLength"
                type="number"
                step="0.01"
                min="0"
                {...register('bodyLength')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bodyWidth">Larg. (m)</Label>
              <Input
                id="bodyWidth"
                type="number"
                step="0.01"
                min="0"
                {...register('bodyWidth')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bodyHeight">Alt. (m)</Label>
              <Input
                id="bodyHeight"
                type="number"
                step="0.01"
                min="0"
                {...register('bodyHeight')}
              />
            </div>
          </div>
        </FormSection>

        {/* Documentos */}
        <FormSection title="Documentos">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="crlvNumber">Nº CRLV</Label>
              <Input id="crlvNumber" {...register('crlvNumber')} />
            </div>
            <DatePicker control={control} name="crlvExpiry" label="Validade CRLV" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="insuranceCompany">Seguradora</Label>
              <Input id="insuranceCompany" {...register('insuranceCompany')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="insurancePolicy">Apólice</Label>
              <Input id="insurancePolicy" {...register('insurancePolicy')} />
            </div>
            <DatePicker
              control={control}
              name="insuranceExpiry"
              label="Validade seguro"
            />
          </div>
        </FormSection>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/vehicles')}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Salvando…"
          >
            {isEdit ? 'Salvar alterações' : 'Cadastrar veículo'}
          </LoadingButton>
        </div>
      </form>
    </div>
  )
}
